import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Calendar, CheckCircle, AlertTriangle, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function WomensHealthGuide() {
  const navigate = useNavigate();

  const screenings = [
    {
      name: "Annual Physical Exam",
      frequency: "Every year",
      includes: ["Blood pressure check", "Weight and BMI", "Heart and lung exam", "Medication review", "Lifestyle discussion"],
      why: "Establishes baseline health metrics and catches changes early"
    },
    {
      name: "Well-Woman Exam",
      frequency: "Every year",
      includes: ["Breast exam", "Pelvic exam", "Discussion of menstrual/menopausal symptoms", "Sexual health discussion", "Contraception review if needed"],
      why: "Addresses female-specific health concerns and hormone changes"
    },
    {
      name: "Mammogram",
      frequency: "Every 1-2 years starting at 40",
      includes: ["Breast X-ray imaging", "Usually takes 20 minutes", "Some discomfort but brief"],
      why: "Detects breast cancer early when it's most treatable. 1 in 8 women will develop breast cancer"
    },
    {
      name: "Pap Smear & HPV Test",
      frequency: "Every 3-5 years (ages 30-65)",
      includes: ["Cervical cell collection", "HPV testing", "Quick procedure"],
      why: "Screens for cervical cancer and pre-cancerous changes"
    },
    {
      name: "Blood Work Panel",
      frequency: "Every 1-3 years",
      includes: ["Complete blood count (CBC)", "Lipid panel (cholesterol)", "Blood glucose/A1C", "Thyroid function (TSH)", "Vitamin D levels", "Iron/ferritin"],
      why: "Reveals hidden issues like thyroid problems, diabetes risk, anemia, and vitamin deficiencies"
    },
    {
      name: "Bone Density Scan (DEXA)",
      frequency: "Baseline at 65 (earlier if risk factors)",
      includes: ["Low-dose X-ray of hip and spine", "Painless, takes 10-20 minutes"],
      why: "Detects osteoporosis before fractures occur. Earlier screening if family history, early menopause, or steroid use"
    },
    {
      name: "Colonoscopy",
      frequency: "Starting at 45, every 10 years",
      includes: ["Colon examination", "Requires prep day before", "Sedation provided"],
      why: "Colorectal cancer is highly preventable when polyps are found and removed early"
    },
    {
      name: "Skin Cancer Screening",
      frequency: "Every year (or as recommended)",
      includes: ["Full-body skin examination", "Mole mapping if needed"],
      why: "Skin cancer is the most common cancer. Early detection is crucial"
    }
  ];

  const questions = [
    "How have I been feeling overall?",
    "Any changes in energy, mood, or sleep?",
    "Any new or unusual symptoms?",
    "Family health history updates?",
    "Current medications and supplements?",
    "Any concerns about perimenopause/menopause?",
    "Sexual health concerns?",
    "Mental health check-in needed?"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-semibold mb-4">
            Week 2 • Day 1 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Women's Health Screenings
          </h1>
          <p className="text-xl text-secondary-600">Your Complete Guide to Preventive Care in Your 40s</p>
        </div>

        {/* Call to Action */}
        <section className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-2xl p-6 text-white mb-8">
          <div className="flex items-center gap-4">
            <Phone className="w-12 h-12 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-xl mb-1">Your Action Step Today</h3>
              <p className="text-pink-100">Call your doctor's office RIGHT NOW and schedule your annual wellness visit. Don't close this page until you've made the call or set a reminder.</p>
            </div>
          </div>
        </section>

        {/* Why It Matters */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why This Matters</h2>
          <p className="text-secondary-700 mb-4">
            Your 40s are a pivotal decade for health. Hormone changes begin, disease risks increase, and your body needs different care than it did in your 20s and 30s. The good news? <strong>Most serious health conditions are preventable or highly treatable when caught early.</strong>
          </p>
          <div className="bg-pink-50 rounded-xl p-4 border border-pink-200">
            <p className="text-secondary-800 font-medium flex items-start gap-2">
              <Heart className="w-5 h-5 text-pink-500 mt-1 flex-shrink-0" />
              Regular screenings can catch up to 90% of cancers at early, treatable stages. This single appointment could literally save your life.
            </p>
          </div>
        </section>

        {/* Screenings List */}
        <section className="mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Recommended Screenings for Women 40+</h2>
          <div className="space-y-4">
            {screenings.map((screening, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-bold text-secondary-900">{screening.name}</h3>
                  <span className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-sm font-medium">
                    {screening.frequency}
                  </span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-semibold text-secondary-500 mb-2">What's Included:</p>
                    <ul className="space-y-1">
                      {screening.includes.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-secondary-700">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-pink-50 rounded-lg p-3">
                    <p className="text-sm text-pink-800"><strong>Why it matters:</strong> {screening.why}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Questions to Ask */}
        <section className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 border-2 border-pink-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Questions to Prepare</h2>
          <p className="text-secondary-600 mb-4">Before your appointment, reflect on these questions:</p>
          <div className="grid md:grid-cols-2 gap-3">
            {questions.map((q, index) => (
              <div key={index} className="bg-white rounded-lg p-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-pink-500 flex-shrink-0" />
                <span className="text-sm text-secondary-700">{q}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Red Flags */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
            <h2 className="text-2xl font-serif font-bold text-secondary-900">Don't Wait If You Notice</h2>
          </div>
          <p className="text-secondary-600 mb-4">Schedule an appointment sooner if you experience:</p>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              "Unexplained weight loss or gain",
              "Persistent fatigue not relieved by rest",
              "Changes in breast tissue or lumps",
              "Unusual bleeding or discharge",
              "Persistent pain anywhere",
              "Changes in bowel or bladder habits",
              "Skin changes or new moles",
              "Severe mood changes or anxiety"
            ].map((symptom, index) => (
              <div key={index} className="flex items-center gap-2 text-sm text-secondary-700">
                <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                {symptom}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default WomensHealthGuide;
